import {
  createContext,
  useContext,
  useEffect,
  useEffectEvent,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useGT } from 'gt-react';
import type { WorkbookSopUpdate } from '@erp/rpc';
import type { WorkbookDefinition } from '@erp/shared-types';
import { orpc, rpcClient } from '@/api/rpc/client';
import { toast } from '@/components/ui/toast';
import { useDebounce } from '@/hooks/useDebounce';
import type { WorkbookEditor } from './useWorkbookEditor';
import {
  applicableSopUpdates,
  sopUpdateCandidates,
  unavailableSopReviews,
  sopUpdatePatch,
} from './workbookSopUpdates';

/** How long autosaves must pause before the saved Revision is a boundary worth reviewing. */
export const SOP_REVIEW_PAUSE_MS = 20_000;
/** How often to look for a review's proposals while a stale Instruction has none. */
const SOP_POLL_MS = 15_000;
/** How long after a boundary (a new Revision or Run) a review's proposals are worth waiting for. */
const SOP_POLL_WINDOW_MS = 180_000;

type ProcessStep = WorkbookDefinition['processSteps'][number];

/** Some stale Instruction's review has neither a proposal nor a verdict yet. */
function awaitingReview(
  definition: WorkbookDefinition,
  candidates: readonly string[],
  updates: readonly WorkbookSopUpdate[],
): boolean {
  const proposals = applicableSopUpdates(definition, updates);
  const unavailable = unavailableSopReviews(definition, updates);
  return candidates.some((id) => !proposals.has(id) && !unavailable.has(id));
}

/** The editor's SOP updates: pending rewrites of stale Instructions and what to do with them. */
export interface WorkbookSopUpdates {
  /** Stale Instructions an SOP update can rewrite (settings changed, text did not). */
  candidates: readonly string[];
  /** The pending proposal of each, when the review has one. */
  proposals: ReadonlyMap<string, WorkbookSopUpdate>;
  /** The review of each that reached no verdict, and why. */
  unavailable: ReadonlyMap<string, WorkbookSopUpdate>;
  /** Instructions whose rewrite the user asked for and is being written. */
  regenerating: ReadonlySet<string>;
  /** The Instructions in the preview, or null when it is closed. */
  previewIds: readonly string[] | null;
  error: string | null;
  /** Opens the preview of these Instructions' rewrites, writing any not proposed yet. */
  regenerate: (stepIds: readonly string[]) => Promise<void>;
  closePreview: () => void;
  accept: (updates: readonly WorkbookSopUpdate[]) => void;
  discard: (update: WorkbookSopUpdate) => void;
}

export const WorkbookSopUpdatesContext =
  createContext<WorkbookSopUpdates | null>(null);

/** The editor's SOP updates, where a surface offers them (null in isolation). */
export function useSopUpdates(): WorkbookSopUpdates | null {
  return useContext(WorkbookSopUpdatesContext);
}

/**
 * SOP updates for the open Workbook. Asks for a review once autosaves pause on a
 * new Revision (Runs and Save Version ask on the server), polls for proposals
 * while a stale Instruction has none, and applies an accepted one through the
 * editor with an Undo. With `sopAutoApply` on, proposals apply as they arrive,
 * each announced and undoable; the user's own text is never replaced silently.
 */
export function useWorkbookSopUpdates(
  editor: Pick<
    WorkbookEditor,
    'workbookId' | 'definition' | 'edits' | 'revision' | 'dirty'
  >,
  canUpdate: boolean,
  /** The newest Run: starting one queues a review on the server. */
  latestRunId?: string,
): WorkbookSopUpdates {
  const gt = useGT();
  const queryClient = useQueryClient();
  const { workbookId, definition, edits, revision, dirty } = editor;
  const candidates = useMemo(
    () => sopUpdateCandidates(definition),
    [definition],
  );
  // Poll only for a while after a boundary: a Match check that finds the text
  // still matching, or is unavailable, leaves a Stale Instruction with no proposal.
  const pollUntil = useRef(0);
  useEffect(() => {
    pollUntil.current = Date.now() + SOP_POLL_WINDOW_MS;
  }, [revision, latestRunId]);
  const listKey = orpc.workbooks.sopUpdates.queryOptions({
    input: { workbookId },
  }).queryKey;
  const list = useQuery({
    ...orpc.workbooks.sopUpdates.queryOptions({ input: { workbookId } }),
    enabled: canUpdate,
    refetchInterval: (query) =>
      awaitingReview(definition, candidates, query.state.data ?? []) &&
      Date.now() < pollUntil.current
        ? SOP_POLL_MS
        : false,
  });
  const proposals = useMemo(
    () => applicableSopUpdates(definition, list.data ?? []),
    [definition, list.data],
  );
  const unavailable = useMemo(
    () => unavailableSopReviews(definition, list.data ?? []),
    [definition, list.data],
  );

  // The Revision open when the editor loaded is not a boundary; later ones are, once saves pause.
  const settled = useDebounce(revision, SOP_REVIEW_PAUSE_MS);
  const reviewed = useRef(revision);
  useEffect(() => {
    if (!canUpdate || dirty || settled !== revision) return;
    if (reviewed.current === revision || !candidates.length) return;
    reviewed.current = revision;
    void rpcClient.workbooks.reviewSop({ workbookId }).catch(() => undefined);
  }, [canUpdate, dirty, settled, revision, candidates.length, workbookId]);

  const [previewIds, setPreviewIds] = useState<readonly string[] | null>(null);
  const [regenerating, setRegenerating] = useState<ReadonlySet<string>>(
    () => new Set(),
  );
  const [error, setError] = useState<string | null>(null);

  function removeFromList(ids: readonly string[]) {
    void queryClient.cancelQueries({ queryKey: listKey });
    queryClient.setQueryData(listKey, (old: WorkbookSopUpdate[] = []) =>
      old.filter((update) => !ids.includes(update.id)),
    );
    setPreviewIds((open) => {
      const left = open?.filter((stepId) =>
        [...proposals.values()].some(
          (update) => update.stepId === stepId && !ids.includes(update.id),
        ),
      );
      return left?.length ? left : null;
    });
  }
  function resolve(
    update: WorkbookSopUpdate,
    resolution: 'accept' | 'discard' | 'reopen',
  ) {
    return rpcClient.workbooks
      .resolveSopUpdate({ workbookId, updateId: update.id, resolution })
      .catch(() => undefined);
  }

  function accept(updates: readonly WorkbookSopUpdate[], automatic = false) {
    const previous = updates.flatMap((update): ProcessStep[] => {
      const step = definition.processSteps.find(
        (item) => item.id === update.stepId,
      );
      return step ? [step] : [];
    });
    for (const update of updates)
      edits.step(update.stepId).applySopUpdate(sopUpdatePatch(update));
    removeFromList(updates.map((update) => update.id));
    void Promise.all(updates.map((update) => resolve(update, 'accept')));
    toast.success(
      automatic
        ? gt(
            'Updated {count, plural, =1 {# instruction} other {# instructions}} from the settings automatically',
            {
              context: 'workbook sop update auto applied toast',
              count: updates.length,
            },
          )
        : gt(
            'Updated {count, plural, =1 {# instruction} other {# instructions}} from the settings',
            {
              context: 'workbook sop update applied toast',
              count: updates.length,
            },
          ),
      {
        action: {
          label: gt('Undo', { context: 'workbook sop update undo' }),
          onClick: () => {
            for (const step of previous) edits.step(step.id).restore(step);
            void Promise.all(
              updates.map((update) => resolve(update, 'reopen')),
            ).then(() => queryClient.invalidateQueries({ queryKey: listKey }));
          },
        },
      },
    );
  }

  function discard(update: WorkbookSopUpdate) {
    removeFromList([update.id]);
    void resolve(update, 'discard');
  }

  async function regenerate(stepIds: readonly string[]) {
    setError(null);
    // Without a preview open, a failure is announced where the user is.
    const fail = (message: string) =>
      definition.sopAutoApply ? toast.error(message) : setError(message);
    // With automatic updates on, a rewrite applies as it arrives: nothing to preview.
    if (!definition.sopAutoApply) setPreviewIds(stepIds);
    const missing = stepIds.filter((id) => !proposals.has(id));
    if (!missing.length) return;
    setRegenerating(new Set(missing));
    try {
      const written = await rpcClient.workbooks.requestSopUpdates({
        workbookId,
        definition,
        stepIds: missing,
      });
      // A list read in flight predates these proposals; it must not overwrite them.
      await queryClient.cancelQueries({ queryKey: listKey });
      queryClient.setQueryData(listKey, (old: WorkbookSopUpdate[] = []) => [
        ...old.filter(
          (update) => !written.some((item) => item.stepId === update.stepId),
        ),
        ...written,
      ]);
      if (written.length < missing.length)
        fail(
          gt(
            "Some instructions couldn't be regenerated. Try again, or edit them yourself.",
            { context: 'workbook sop update partial failure' },
          ),
        );
    } catch {
      fail(
        gt("The instructions couldn't be regenerated. Try again.", {
          context: 'workbook sop update failure',
        }),
      );
    }
    setRegenerating(new Set());
  }

  const autoApplied = useRef(new Set<string>());
  const applyArrived = useEffectEvent(() => {
    if (!canUpdate || !definition.sopAutoApply) return;
    const arrived = [...proposals.values()].filter(
      (update) => !autoApplied.current.has(update.id),
    );
    if (!arrived.length) return;
    for (const update of arrived) autoApplied.current.add(update.id);
    accept(arrived, true);
  });
  useEffect(() => applyArrived(), [proposals, definition.sopAutoApply]);

  return {
    candidates,
    proposals,
    unavailable,
    regenerating,
    previewIds,
    error,
    regenerate,
    closePreview: () => {
      setPreviewIds(null);
      setError(null);
    },
    accept,
    discard,
  };
}
